import Swiper from "swiper";
import { Navigation, Pagination, Thumbs, Manipulation } from "swiper/modules";
import { isFavorite, toggleFavorite } from "@src/utils/favorite";

import "./index.css";

export function setupGallery() {
  const selector = ".container-gallery";
  const containers = document.querySelectorAll(selector);
  if (containers.length > 0) {
    containers.forEach((container) => {
      // update favorite
      container
        ?.querySelector(".container-rooms-favorite")
        ?.addEventListener("click", () => {
          toggleFavorite(container);
        });

      const galleryImages = container.querySelector(
        ".container-gallery-images"
      );

      if (galleryImages) {
        // add gallery tabs to container
        const galleryTabs = document.createElement("div");
        galleryTabs.classList.add("container-gallery-tabs");
        container
          .querySelectorAll<HTMLElement>(".container-gallery-data-group")
          .forEach((group) => {
            if (group.dataset.name) {
              const tab = document.createElement("div");
              tab.classList.add("container-gallery-tabs-item");
              tab.setAttribute("data-name", group.dataset.name);
              tab.innerHTML = `<span>${group.dataset.name}</span>`;
              galleryTabs?.appendChild(tab);
            }
          });
        if (galleryTabs.children.length > 0) {
          container.prepend(galleryTabs);
        }

        // init swiper
        const imagesSwiper = new Swiper(
          galleryImages.querySelector(
            ".container-gallery-images-swiper"
          ) as HTMLElement,
          {
            slidesPerView: 1,
            spaceBetween: 24,
            modules: [Navigation, Pagination, Thumbs, Manipulation],
            pagination: {
              el: galleryImages.querySelector(
                ".swiper-pagination"
              ) as HTMLElement,
              type: "fraction",
            },
            navigation: {
              nextEl: galleryImages.querySelector(
                ".swiper-button-next"
              ) as HTMLElement,
              prevEl: galleryImages.querySelector(
                ".swiper-button-prev"
              ) as HTMLElement,
            },
          }
        );

        // Cache next/prev buttons for reuse
        const nextButton = imagesSwiper.navigation.nextEl as HTMLElement;
        const prevButton = imagesSwiper.navigation.prevEl as HTMLElement;

        // Function to update navigation button styles
        const updateNavigationButtons = () => {
          // Determine if next/prev buttons should remain active
          const currentTab = container.querySelector<HTMLElement>(
            ".container-gallery-tabs-item[data-selected]"
          );
          const nextTab = currentTab?.nextElementSibling as HTMLElement;
          const prevTab = currentTab?.previousElementSibling as HTMLElement;

          if (imagesSwiper.isEnd && !nextTab) {
            nextButton?.classList.add("swiper-button-disabled");
          } else {
            nextButton?.classList.remove("swiper-button-disabled");
          }

          if (imagesSwiper.isBeginning && !prevTab) {
            prevButton?.classList.add("swiper-button-disabled");
          } else {
            prevButton?.classList.remove("swiper-button-disabled");
          }
        };

        // update swiper slide
        const updateSwiper = (group: HTMLElement) => {
          // clear swiper
          imagesSwiper.removeAllSlides();
          // update swiper wrapper
          const images = galleryImages?.querySelector(".swiper-wrapper");

          if (group && images) {
            const html = Array.from(group.children).map(
              (child) => child.outerHTML
            );
            imagesSwiper.appendSlide(html);
            imagesSwiper.slideTo(0, 0); // Reset to first slide
          }

          // set favorite data
          if (group.dataset.propertyId) {
            const propertyId = group.dataset.propertyId;
            if (propertyId) {
              container?.setAttribute("data-property-id", propertyId);
              container?.setAttribute(
                "data-favorite",
                isFavorite(propertyId).toString()
              );
            }
          }

          // set scroll button
          const scrollToName = group.dataset.scrollToName;
          const scrollButton = container.querySelector(
            '.container-gallery-section-button[data-popup-id="showcase-gallery"]'
          );
          if (scrollToName) {
            scrollButton?.setAttribute("data-scroll-to-name", scrollToName);
          } else {
            scrollButton?.removeAttribute("data-scroll-to-name");
          }

          updateNavigationButtons(); // Update navigation buttons after swiper update
        };

        // change select tab
        const selectTab = (name: string) => {
          const group = container.querySelector<HTMLElement>(
            `.container-gallery-data-group[data-name="${name}"]`
          );
          if (!group) {
            return;
          }

          // update tabs
          container
            .querySelector(".container-gallery-tabs-item[data-selected]")
            ?.removeAttribute("data-selected");
          container
            .querySelector(`.container-gallery-tabs-item[data-name="${name}"]`)
            ?.setAttribute("data-selected", "");

          updateSwiper(group);

          // Ensure swiper starts at the first slide
          imagesSwiper.slideTo(0, 0);
        };

        // Handle slide transitions and edge detection
        let atLastSlide = false;
        let atFirstSlide = false;

        imagesSwiper.on("fromEdge", () => {
          // Reset flags when user leaves edge
          atLastSlide = false;
          atFirstSlide = false;
        });

        imagesSwiper.on("slideChangeTransitionEnd", () => {
          if (imagesSwiper.isEnd) {
            // Mark that we are at the last slide
            atLastSlide = true;
          } else if (imagesSwiper.isBeginning) {
            // Mark that we are at the first slide
            atFirstSlide = true;
          } else {
            // Reset flags if not at edges
            atLastSlide = false;
            atFirstSlide = false;
          }
          updateNavigationButtons(); // Update navigation buttons on slide change
        });

        // Handle edge slide events
        const handleEdgeSlide = () => {
          if (atLastSlide) {
            // Switch to next tab
            const currentTab = container.querySelector<HTMLElement>(
              ".container-gallery-tabs-item[data-selected]"
            );
            const nextTab = currentTab?.nextElementSibling as HTMLElement;
            if (nextTab) {
              selectTab(nextTab.dataset.name || "");
              imagesSwiper.allowSlideNext = false;
              imagesSwiper.allowSlidePrev = false;
              setTimeout(() => {
                imagesSwiper.allowSlideNext = true;
                imagesSwiper.allowSlidePrev = true;
              }, 300); // Delay to prevent unexpected slide changes
            }
          } else if (atFirstSlide) {
            // Switch to previous tab
            const currentTab = container.querySelector<HTMLElement>(
              ".container-gallery-tabs-item[data-selected]"
            );
            const prevTab = currentTab?.previousElementSibling as HTMLElement;
            if (prevTab) {
              selectTab(prevTab.dataset.name || "");
              imagesSwiper.allowSlideNext = false;
              imagesSwiper.allowSlidePrev = false;
              setTimeout(() => {
                imagesSwiper.allowSlideNext = true;
                imagesSwiper.allowSlidePrev = true;
              }, 300); // Delay to prevent unexpected slide changes
            }
          }
          updateNavigationButtons(); // Update navigation buttons on edge slide
        };

        // Add event listeners for next/prev button and manual swipe
        imagesSwiper.on("touchEnd", handleEdgeSlide); // Detect when user finishes swiping
        nextButton?.addEventListener("click", handleEdgeSlide);
        prevButton?.addEventListener("click", handleEdgeSlide);

        // select first tab
        selectTab(
          container
            .querySelector(".container-gallery-tabs-item")
            ?.getAttribute("data-name") || ""
        );

        // add event listener to tabs
        container
          .querySelector(".container-gallery-tabs")
          ?.addEventListener("click", (event) => {
            if (event.target instanceof HTMLElement) {
              const tab = event.target.closest(".container-gallery-tabs-item");
              if (tab) {
                selectTab(tab.getAttribute("data-name") || "");
              }
            }
          });

        // select room gallery
        const selectRoom = (name: string) => {
          const room = container.querySelector<HTMLElement>(
            `.container-gallery-data-room[data-name="${name}"]`
          );
          if (room) {
            updateSwiper(room);
          }
        };

        // add event listener to open gallery
        document.addEventListener("click", (event) => {
          if (event.target instanceof HTMLElement) {
            const gallery = event.target.closest("[data-gallery-open]");
            if (gallery) {
              selectRoom(gallery.getAttribute("data-gallery-open") || "");
            }
          }
        });
      }
    });
  }
}
